import React, { useEffect } from 'react';
import FormModal from '../../components/Modals/FormModal';
import SelectField from '../../components/Forms/Input/SelectField';

import { useCustomer } from '../../hooks/useCustomer';
import { useEntityType } from '../../hooks/useEntityType';
import { useTaxpayerCategory } from '../../hooks/useTaxpayerCategory';
import { useEconomicActivities } from '../../hooks/useEconomicActivities';
import { useLocation } from '../../hooks/useLocation';

import CustomerNaturalForm from './forms/CustomerNaturalForm';
import CustomerJuridicoForm from './forms/CustomerJuridicoForm';
import CustomerConsumidorFinalForm from './forms/CustomerConsumidorFinalForm';
import { useDocumentIdentificationType } from '../../hooks/documentIdentificationType/useDocumentIdentificationType';
import CustomerGeneralForm from './forms/CustomerGeneralForm';
import CustomerFormPlaceholder from './CustomerFormPlaceholder';
import { useCountry } from '../../hooks/countries/useCountry';
import ToggleSwitch from '../../components/Forms/Input/ToggleSwitch';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange?: (open: boolean) => void;
  onSubmit?: () => void;
}

const CustomerRegistrationPopup: React.FC<Props> = ({
  isOpen,
  onClose,
  onOpenChange,
  onSubmit,
}) => {
  const { form, isSubmitting, updateFormField, resetForm, changeEntityType } =
    useCustomer();

  // Dropdowns
  const { fetchEntityTypeDropdown, entityTypeDropdown } = useEntityType();
  const { fetchTaxpayerCategoryDropdown, taxpayerCategoryDropdown } =
    useTaxpayerCategory();
  const {
    handlerAddEconomicActivity,
    fetchEconomicActivitiesDropdown,
    economicActivitiesDropdown,
    reloadEconomicActivitiesDropdown,
  } = useEconomicActivities();
  const {
    fetchDocumentIdentificationTypeDropdown,
    documentIdentificationTypeDropdown,
  } = useDocumentIdentificationType();

  const {
    fetchDepartments,
    fetchMunicipalitiesByDepartment,
    departments,
    municipalities,
  } = useLocation();

  const { countryDropdown, fetchCountryDropdown } = useCountry();

  // Al abrir modal: limpiar form y cargar dropdowns
  useEffect(() => {
    if (isOpen) {
      resetForm();
      fetchEntityTypeDropdown();
      fetchTaxpayerCategoryDropdown();
      fetchEconomicActivitiesDropdown();
      fetchDepartments();
      fetchDocumentIdentificationTypeDropdown();
      fetchCountryDropdown('');
    }
  }, [isOpen]);

  const entityTypeId = Number(form.entityTypeId);
  const handleClose = () => {
    if (onOpenChange) {
      onOpenChange(false);
      return;
    }
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Registrar Cliente"
      maxWidth="max-w-6xl"
    >
      <div className="mt-4 space-y-8">
        {/* 🔹 Sección: Tipo de Entidad */}
        <div>
          <div className="mb-6 border-b pb-4">
            <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
              Selecciona el Tipo de Entidad
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Esta selección define los campos que deberás completar a
              continuación. Al elegir el tipo de entidad correcto, el sistema
              mostrará automáticamente el formulario específico para registrar
              al cliente.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <SelectField
              iconKey="list"
              label="Tipo de Entidad"
              name="entityTypeId"
              value={form.entityTypeId}
              onChange={(e) => changeEntityType(Number(e.target.value))}
              options={entityTypeDropdown
                .filter((t) => t.id !== 4)
                .map((t) => ({
                  label: t.name,
                  value: t.id,
                }))}
            />
          </div>
        </div>

        {/* 🔹 Sección dinámica según tipo */}
        {entityTypeId ? (
          <div>
            <div className="mb-6 border-b pb-4">
              <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
                Datos del Cliente
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Completa la información requerida según el tipo de cliente
                seleccionado.
              </p>
            </div>

            <div className="mb-6 rounded-xl border border-stroke bg-gray-2/50 p-4 dark:border-strokedark dark:bg-meta-4/30">
              <ToggleSwitch
                label="Cliente predeterminado para ventas y restaurante"
                name="defaultCustomer"
                checked={form.defaultCustomer}
                disabled={isSubmitting}
                onChange={() =>
                  updateFormField('defaultCustomer', !form.defaultCustomer)
                }
              />
            </div>

            {entityTypeId === 1 && (
              <CustomerNaturalForm
                form={form}
                updateField={updateFormField}
                taxpayerCategoryDropdown={taxpayerCategoryDropdown}
                economicActivitiesDropdown={economicActivitiesDropdown}
                countryDropdown={countryDropdown}
                departments={departments}
                municipalities={municipalities}
                documentIdentificationTypeDropdown={
                  documentIdentificationTypeDropdown
                }
                onFetchMunicipalities={fetchMunicipalitiesByDepartment}
                onCancel={handleClose}
                onSubmit={onSubmit}
                onEconomicActivityCreated={handlerAddEconomicActivity}
                onReloadEconomicActivities={reloadEconomicActivitiesDropdown}
                isSubmitting={isSubmitting}
              />
            )}

            {entityTypeId === 2 && (
              <CustomerJuridicoForm
                form={form}
                updateField={updateFormField}
                taxpayerCategoryDropdown={taxpayerCategoryDropdown}
                economicActivitiesDropdown={economicActivitiesDropdown}
                countryDropdown={countryDropdown}
                departments={departments}
                municipalities={municipalities}
                documentIdentificationTypeDropdown={
                  documentIdentificationTypeDropdown
                }
                onFetchMunicipalities={fetchMunicipalitiesByDepartment}
                onCancel={handleClose}
                onSubmit={onSubmit}
                onEconomicActivityCreated={handlerAddEconomicActivity}
                onReloadEconomicActivities={reloadEconomicActivitiesDropdown}
                isSubmitting={isSubmitting}
              />
            )}

            {entityTypeId === 3 && (
              <CustomerConsumidorFinalForm
                form={form}
                updateField={updateFormField}
                countryDropdown={countryDropdown}
                departments={departments}
                municipalities={municipalities}
                documentIdentificationTypeDropdown={
                  documentIdentificationTypeDropdown
                }
                onFetchMunicipalities={fetchMunicipalitiesByDepartment}
                onCancel={handleClose}
                onSubmit={onSubmit}
                isSubmitting={isSubmitting}
              />
            )}

            {entityTypeId === 5 && (
              <CustomerGeneralForm
                form={form}
                updateField={updateFormField}
                onCancel={handleClose}
                onSubmit={onSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        ) : (
          <CustomerFormPlaceholder />
        )}
      </div>
    </FormModal>
  );
};

export default CustomerRegistrationPopup;
